import React from 'react';
import {
  useGetJobsiteCardFormatsQuery,
  WorkerCardType,
} from 'apollo/generated/client-operations';
import { getSingleOrNull, typedAs } from 'utils';
import {
  FormInputTypes,
  GridColSpan,
  TypedFormInputs,
  UseFormMethods,
  UseInputs,
} from 'components/form';
import { IdCardIcon } from 'components/icons';
import { QrCodeField, QrCodeFieldProps } from 'components/qrCodeField';
import { validateScannedBadgeQrCode } from 'containers/utils';
import {
  CARD_WITH_SCANNABLE_NUMBER,
  getBadgeNumberValidation,
} from 'containers/worker/modals/badgeModal/utils';
import { JobsiteCardFormatOption, RegisterTempBadgeFormData } from './types';

export const useCardFormatOptions = (
  jobsiteId: string,
): {
  cardFormatOptions: JobsiteCardFormatOption[];
  defaultCardFormatOption: JobsiteCardFormatOption | null;
} => {
  const { data } = useGetJobsiteCardFormatsQuery({
    fetchPolicy: 'no-cache',
    skip: !jobsiteId,
    variables: { jobsiteId },
  });

  return React.useMemo(() => {
    if (!data) return { cardFormatOptions: [], defaultCardFormatOption: null };
    const jobsiteCardFormats = data.getJobsite?.jobsiteCardFormats.edges
      .map(({ node }) => node)
      .filter(
        ({ workerCardFormat }) =>
          workerCardFormat?.cardType === WorkerCardType.Proximity,
      )
      .sort(({ isDefaultForCardType }) => (isDefaultForCardType ? -1 : 1));

    const cardFormatOptions = jobsiteCardFormats?.map(
      ({
        jobsiteCardFormatId,
        isDefaultForCardType,
        workerCardFormat: {
          name,
          facilityCode,
          encoding,
          validationRegex,
          validationMessage,
        },
      }) => ({
        value: jobsiteCardFormatId,
        label: [name, facilityCode].filter(Boolean).join(' - '),
        isDefault: !!isDefaultForCardType,
        name,
        encoding,
        validationRegex,
        validationMessage,
      }),
    );

    return {
      cardFormatOptions,
      defaultCardFormatOption:
        cardFormatOptions?.find(({ isDefault }) => isDefault) ?? null,
    };
  }, [data]);
};

const getFormInputs = ({
  cardFormatOptions,
  allowScanning,
  validationRegex,
  validationMessage,
  encoding,
}: {
  cardFormatOptions: JobsiteCardFormatOption[];
  allowScanning: boolean;
  validationRegex?: string;
  validationMessage?: string;
  encoding?: string;
}): TypedFormInputs<RegisterTempBadgeFormData> => ({
  jobsiteCardFormatId: {
    element: FormInputTypes.Select,
    label: 'Card format',
    elementProps: {
      options: cardFormatOptions,
      disabled: cardFormatOptions.length < 2,
      isClearable: false,
    },
    validation: { required: true },
    layout: GridColSpan.SpanFull,
  },
  badgeNumber: {
    element: FormInputTypes.CustomInput,
    label: 'Badge Number',
    elementProps: {
      customInput: QrCodeField,
      // @ts-expect-error [it's a fine icon]
      icon: IdCardIcon,
      placeholder: allowScanning ? 'Enter or scan badge number' : null,
      ...typedAs<Omit<QrCodeFieldProps, 'name'>>({
        allowScanning,
        parseQrCode: validateScannedBadgeQrCode,
      }),
    },
    validation: getBadgeNumberValidation({
      validationRegex,
      validationMessage,
      encoding,
    }),
    layout: GridColSpan.SpanFull,
  },
});

export const getFormInputsHook =
  (args: {
    cardFormatOptions: JobsiteCardFormatOption[];
  }): UseInputs<RegisterTempBadgeFormData> =>
  (
    form: UseFormMethods<RegisterTempBadgeFormData>,
  ): TypedFormInputs<RegisterTempBadgeFormData> => {
    const { cardFormatOptions } = args;
    const { watch } = form;
    const jobsiteCardFormatIdElement = watch('jobsiteCardFormatId') as
      | JobsiteCardFormatOption
      | null
      | undefined;
    const allowScanning =
      jobsiteCardFormatIdElement?.name === CARD_WITH_SCANNABLE_NUMBER;

    React.useEffect(() => {
      if (jobsiteCardFormatIdElement?.name === CARD_WITH_SCANNABLE_NUMBER) {
        const badgeNumberFieldRef = form.getFieldRef('badgeNumber');
        badgeNumberFieldRef?.open?.();
      }
    }, [jobsiteCardFormatIdElement?.name]);

    return React.useMemo(
      () =>
        getFormInputs({
          cardFormatOptions,
          allowScanning,
          validationRegex: jobsiteCardFormatIdElement?.validationRegex,
          validationMessage: jobsiteCardFormatIdElement?.validationMessage,
          encoding: jobsiteCardFormatIdElement?.encoding,
        }),
      [
        cardFormatOptions,
        allowScanning,
        jobsiteCardFormatIdElement?.validationRegex,
        jobsiteCardFormatIdElement?.validationMessage,
        jobsiteCardFormatIdElement?.encoding,
      ],
    );
  };

export const getFormDefaultValues = (args: {
  cardFormatOptions: JobsiteCardFormatOption[];
  defaultCardFormatOption: JobsiteCardFormatOption | null;
}): RegisterTempBadgeFormData => {
  const { cardFormatOptions, defaultCardFormatOption } = args;
  return {
    badgeNumber: '',
    jobsiteCardFormatId:
      defaultCardFormatOption ?? getSingleOrNull(cardFormatOptions),
  };
};
